import { useState, useEffect } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, CheckCircle, XCircle, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AddCommissionModal({ policy, userInfo, open, onOpenChange, onSuccess }) {
  const [formData, setFormData] = useState({
    npn: '',
    commission_amount: '',
    paid_status: '',
    statement_date: '',
    comment: ''
  });
  const [npnVerified, setNpnVerified] = useState(null);
  const [npnVerifying, setNpnVerifying] = useState(false);
  const [verifiedAgentData, setVerifiedAgentData] = useState(null);
  const [paidStatuses, setPaidStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  useEffect(() => {
    if (policy && open) {
      setFormData({
        npn: '',
        commission_amount: '',
        paid_status: '',
        statement_date: '',
        comment: ''
      });
      setNpnVerified(null);
      setVerifiedAgentData(null);
      loadPaidStatuses();
    }
  }, [policy, open]);

  const loadPaidStatuses = async () => {
    try {
      setLoading(true);
      const response = await axios.get('https://api1.simplyworkcrm.com/api:JBYRWqH3/meta/commission/paidSplit');
      const statuses = Array.isArray(response.data) 
        ? response.data.map(s => ({ value: s.id, label: s.status }))
        : [];
      setPaidStatuses(statuses);
      setLoading(false);
    } catch (err) {
      console.error('Error loading paid statuses:', err);
      toast.error('Failed to load paid status options');
      setLoading(false);
    }
  };

  const verifyNPN = async () => {
    if (!formData.npn || formData.npn.length < 3) {
      toast.error('Please enter a valid NPN');
      return;
    }

    try {
      setNpnVerifying(true);
      const response = await axios.post(`${API}/auth/verify-npn`, { npn: formData.npn });
      
      // Handle different response structures
      const isValid = response.data?.found === true || 
                      response.data?.success === true || 
                      (response.data && Object.keys(response.data).length > 0 && !response.data.error);
      
      if (isValid) {
        setNpnVerified(true);
        setVerifiedAgentData(response.data);
        toast.success('NPN verified successfully');
      } else {
        setNpnVerified(false);
        setVerifiedAgentData(null);
        toast.error('NPN not found or invalid');
      }
    } catch (err) {
      console.error('Error verifying NPN:', err);
      setNpnVerified(false);
      setVerifiedAgentData(null);
      toast.error('Failed to verify NPN');
    } finally {
      setNpnVerifying(false);
    }
  };

  const handleNpnChange = (value) => {
    setFormData({ ...formData, npn: value });
    setNpnVerified(null);
    setVerifiedAgentData(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!npnVerified) {
      toast.error('Please verify the NPN first');
      return;
    }

    if (!formData.commission_amount) {
      toast.error('Please enter a commission amount');
      return;
    }

    if (!formData.paid_status) {
      toast.error('Please select a paid status');
      return;
    }

    setShowConfirmation(true);
  };

  const confirmSubmit = async () => {
    try {
      setSaving(true);
      
      const formDataObject = {
        agent_on_commission_npn: formData.npn || null,
        commission_amount: formData.commission_amount ? parseFloat(formData.commission_amount) : null,
        paid_split: formData.paid_status || null,
        statement_date: formData.statement_date || null,
        comment: formData.comment || null,
        submitted_by: userInfo?.user_id || null
      };

      const response = await axios.post(
        'https://api1.simplyworkcrm.com/api:JBYRWqH3/portal/internal/commission/add',
        {
          policy_id: policy.id,
          formData: formDataObject
        }
      );

      toast.success('Commission added successfully');
      onSuccess && onSuccess();
      setShowConfirmation(false);
      onOpenChange(false);
    } catch (err) {
      console.error('Error adding commission:', err);
      toast.error('Failed to add commission');
    } finally {
      setSaving(false);
    }
  };

  if (!policy) return null;

  return (
    <>
      <Dialog open={open && !showConfirmation} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>Add Commission</DialogTitle>
            <DialogDescription>
              Add a new commission record for this policy
            </DialogDescription>
          </DialogHeader>

          {/* Scrollable Content */}
          <div className="overflow-y-auto flex-1 pr-2">
            {/* Policy Info Header */}
            <div className="bg-[#fffef0] p-4 rounded-xl border border-[#d4af37] space-y-2 mb-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-neutral-500">Policy Number</p>
                  <p className="text-sm font-semibold text-neutral-800">{policy.policy_number}</p>
                </div>
                <div>
                  <p className="text-xs text-neutral-500">Client Name</p>
                  <p className="text-sm font-semibold text-neutral-800">{policy.client || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-neutral-500">Policy ID</p>
                  <p className="text-xs font-mono text-neutral-600">{policy.id}</p>
                </div>
                <div>
                  <p className="text-xs text-neutral-500">Agent Owner</p>
                  <p className="text-sm font-semibold text-neutral-800">{policy.agentOwner_name}</p>
                </div>
                <div>
                  <p className="text-xs text-neutral-500">Owner NPN</p>
                  <p className="text-sm font-semibold text-neutral-800">{policy.agentOwner_npn}</p>
                </div>
                <div>
                  <p className="text-xs text-neutral-500">Submitted By</p>
                  <p className="text-sm font-semibold text-neutral-800">
                    {userInfo?.first_name && userInfo?.last_name 
                      ? `${userInfo.first_name} ${userInfo.last_name}` 
                      : userInfo?.full_name || 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-neutral-500">User ID</p>
                  <p className="text-xs font-mono text-neutral-600">{userInfo?.user_id || 'N/A'}</p>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-[#d4af37]" />
              </div>
            ) : (
              <form onSubmit={handleSubmit} id="add-commission-form">
              <div className="space-y-4 py-4">
                {/* Agent on Commission NPN */}
                <div className="space-y-2">
                  <Label htmlFor="npn">
                    Agent on Commission NPN <span className="text-red-500">*</span>
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      id="npn"
                      value={formData.npn}
                      onChange={(e) => handleNpnChange(e.target.value)}
                      placeholder="Enter NPN"
                      required
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      onClick={verifyNPN}
                      disabled={npnVerifying || !formData.npn}
                      variant="outline"
                      className="min-w-[100px]"
                    >
                      {npnVerifying ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Verifying
                        </>
                      ) : npnVerified === true ? (
                        <>
                          <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
                          Verified
                        </>
                      ) : npnVerified === false ? (
                        <>
                          <XCircle className="w-4 h-4 mr-2 text-red-600" />
                          Invalid
                        </>
                      ) : (
                        'Verify'
                      )}
                    </Button>
                  </div>
                  {npnVerified === false && (
                    <p className="text-xs text-red-600 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      NPN not found. Please verify the NPN is correct.
                    </p>
                  )}
                  {npnVerified === true && verifiedAgentData && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <p className="text-sm font-semibold text-green-800">
                        ✓ Agent Verified
                      </p>
                      <p className="text-sm text-green-700 mt-1">
                        {verifiedAgentData.first_name} {verifiedAgentData.last_name}
                      </p>
                      {verifiedAgentData.status && (
                        <p className="text-xs text-green-600 mt-1">
                          Status: {verifiedAgentData.status}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Commission Amount */}
                <div className="space-y-2">
                  <Label htmlFor="amount">
                    Commission Amount <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="amount"
                    type="number"
                    step="0.01"
                    value={formData.commission_amount}
                    onChange={(e) => setFormData({ ...formData, commission_amount: e.target.value })}
                    placeholder="Enter amount (can be negative)"
                    required
                  />
                  <p className="text-xs text-neutral-500">
                    Accepts decimal and negative numbers (e.g., -34.16 for chargebacks)
                  </p>
                </div>

                {/* Paid Status */}
                <div className="space-y-2">
                  <Label htmlFor="paid_status">
                    Paid Status <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={formData.paid_status}
                    onValueChange={(value) => setFormData({ ...formData, paid_status: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select paid status" />
                    </SelectTrigger>
                    <SelectContent>
                      {paidStatuses.map((status) => (
                        <SelectItem key={status.value} value={status.value}>
                          {status.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Statement Date */}
                <div className="space-y-2">
                  <Label htmlFor="statement_date">Statement Date</Label>
                  <Input
                    id="statement_date"
                    type="date"
                    value={formData.statement_date}
                    onChange={(e) => setFormData({ ...formData, statement_date: e.target.value })}
                  />
                </div>

                {/* Comment */}
                <div className="space-y-2">
                  <Label htmlFor="comment">Comment (Optional)</Label>
                  <Textarea
                    id="comment"
                    value={formData.comment}
                    onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                    placeholder="Add any notes or comments"
                    rows={3}
                  />
                </div>
              </div>
            </form>
            )}
          </div>

          {/* Footer - Fixed at bottom */}
          <DialogFooter className="flex-shrink-0 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="add-commission-form"
              disabled={!npnVerified}
              className="bg-[#d4af37] hover:bg-[#b8941e] text-white"
            >
              Add Commission
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmation} onOpenChange={setShowConfirmation}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Commission</DialogTitle>
            <DialogDescription>
              Please review the commission details before submitting
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-4">
            <div className="flex justify-between">
              <span className="text-sm text-neutral-600">Policy:</span>
              <span className="text-sm font-semibold">{policy.policy_number}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-neutral-600">Agent NPN:</span>
              <span className="text-sm font-semibold">{formData.npn}</span>
            </div>
            {verifiedAgentData && (
              <div className="flex justify-between">
                <span className="text-sm text-neutral-600">Agent Name:</span>
                <span className="text-sm font-semibold text-green-700">
                  {verifiedAgentData.first_name} {verifiedAgentData.last_name}
                </span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-sm text-neutral-600">Amount:</span>
              <span className={`text-sm font-bold ${parseFloat(formData.commission_amount) < 0 ? 'text-red-600' : 'text-green-600'}`}>
                ${parseFloat(formData.commission_amount).toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-neutral-600">Paid Status:</span>
              <span className="text-sm font-semibold">
                {paidStatuses.find(s => s.value === formData.paid_status)?.label}
              </span>
            </div>
            {formData.comment && (
              <div>
                <span className="text-sm text-neutral-600">Comment:</span>
                <p className="text-sm mt-1 text-neutral-800">{formData.comment}</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowConfirmation(false)}
              disabled={saving}
            >
              Back
            </Button>
            <Button
              onClick={confirmSubmit}
              disabled={saving}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Confirm & Submit'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
